import { useState, useEffect } from "react";
import { formatStamp } from "../lib/richText";
import { GlobalSearchBar } from "../components/GlobalSearchBar";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  getDashboardStats, getRecentActivity, getMessageConversations, getTaskBoard, markClientHandled,
  verifyProject, getChangeRequests,
} from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { TaskFreshnessPill } from "../components/TaskFreshnessPill";
import { toast } from "sonner";
import {
  Loader2, ShoppingBag, Users as UsersIcon, Repeat, Archive, RefreshCw, FileSearch,
  Presentation, GraduationCap, Award, Bell, ListChecks, ChevronRight, MessageSquare,
  CheckCircle2, Clock, Check,
} from "lucide-react";

const fmtMoney = (n) => `$${(n || 0).toLocaleString()}`;


function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${Math.max(m, 1)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Compact chip-style nav button used in the rep dashboard
function ActionChip({ label, count, value, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-white/70 hover:bg-white border border-transparent hover:border-zinc-200 transition text-left text-sm"
    >
      <span className="font-medium">{label}</span>
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {count != null && <span className="font-semibold text-foreground bg-zinc-100 rounded-full px-2 py-0.5">{count}</span>}
        {value != null && <span className="text-zinc-600">{value}</span>}
        <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

function MessagesTasksPanel({ conversations, tasks, navigate, isInternalView }) {
  const replyNeeded = conversations.filter((c) => c.reply_needed);
  return (
    <Card className="border bg-gradient-to-br from-orange-50 to-amber-50">
      <CardContent className="pt-5 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-outfit font-semibold flex items-center gap-2 text-orange-900">
              <Bell className="h-4 w-4" /> Reply Needed
              {replyNeeded.length > 0 && (
                <span className="text-[11px] bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">{replyNeeded.length}</span>
              )}
            </h3>
            <button type="button" onClick={() => navigate("/messages")} className="text-xs font-medium text-orange-700 hover:underline" data-testid="open-messages-link">
              Open Inbox →
            </button>
          </div>
          <div className="space-y-1.5">
            {replyNeeded.slice(0, 4).map((c) => (
              <button
                type="button"
                key={c.client_id}
                onClick={() => navigate(`/messages/${c.client_id}`)}
                data-testid={`dash-msg-${c.client_id}`}
                className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white hover:bg-orange-100/40 border border-orange-100 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.client_name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {[c.agency_name, c.rep_name].filter(Boolean).join(" · ")}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    <b>{c.last_author_name || ""}</b>: {c.last_body}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-muted-foreground">{timeAgo(c.last_activity)}</span>
                  <span className="inline-flex items-center justify-center h-5 rounded-full bg-red-500 text-white text-[10px] font-semibold px-2">
                    Reply
                  </span>
                </div>
              </button>
            ))}
            {replyNeeded.length === 0 && (
              <div className="text-xs text-muted-foreground px-3 py-2">All caught up — no replies needed.</div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-outfit font-semibold flex items-center gap-2 text-orange-900">
              <ListChecks className="h-4 w-4" /> Tasks
            </h3>
          </div>
          <div className="space-y-1.5">
            {/* Group tasks by client */}
            {(() => {
              const grouped = {};
              tasks.forEach((t) => {
                const key = t.client_id || "unknown";
                if (!grouped[key]) grouped[key] = { client_name: t.client_name, project_id: t.project_id, count: 0, next: null };
                grouped[key].count += 1;
                if (!grouped[key].next) grouped[key].next = t;
              });
              const rows = Object.values(grouped).slice(0, 5);
              if (rows.length === 0) {
                return <div className="text-xs text-muted-foreground px-3 py-2">No pending tasks.</div>;
              }
              return rows.map((g, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => g.project_id && navigate(`/orders/${g.project_id}`)}
                  className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white hover:bg-amber-100/40 border border-amber-100 text-sm"
                >
                  <div className="min-w-0">
                    <span className="font-medium">{g.client_name || "—"}</span>
                    <span className="text-muted-foreground"> ({g.count} task{g.count === 1 ? "" : "s"}) · Due Next: </span>
                    <span className="text-foreground">{g.next?.title || "—"}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ));
            })()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RepDashboard({ user, stats, conversations, tasks, navigate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* LEFT COLUMN — two stacked boxes */}
      <div className="space-y-5">
        {/* Manage Clients */}
        <Card className="border bg-gradient-to-br from-blue-50 to-sky-50">
          <CardContent className="pt-5">
            <h3 className="font-outfit font-semibold flex items-center gap-2 text-blue-900 mb-3">
              <UsersIcon className="h-4 w-4" /> Manage Clients
            </h3>
            <div className="space-y-1.5">
              <ActionChip label="Book New Order" onClick={() => navigate("/orders/new")} testid="chip-new-order" />
              <ActionChip label="Onboarding Projects" count={stats?.onboarding_count} onClick={() => navigate("/orders/active")} testid="chip-onboarding" />
              <ActionChip label="Monthly Active Clients" count={stats?.monthly_active_count} onClick={() => navigate("/clients/monthly-active")} testid="chip-monthly" />
              <ActionChip label="Upcoming Renewals" count={stats?.expiring_count} value={stats?.expiring_value ? fmtMoney(stats.expiring_value) : null} onClick={() => navigate("/renewals")} testid="chip-renewals" />
              <ActionChip label="Archived Clients" count={stats?.archived_count} onClick={() => navigate("/orders/archived")} testid="chip-archived" />
            </div>
          </CardContent>
        </Card>

        {/* Sell More */}
        <Card className="border bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="pt-5">
            <h3 className="font-outfit font-semibold flex items-center gap-2 text-green-900 mb-3">
              <Award className="h-4 w-4" /> Sell More Clients
            </h3>
            <div className="space-y-1.5">
              <ActionChip label="Build/Request Proposal" onClick={() => navigate("/proposals?new=proposal")} testid="chip-build-proposal" />
              <ActionChip label="Request Client Audit" onClick={() => navigate("/proposals?new=audit")} testid="chip-request-audit" />
              <ActionChip label="View Pending Proposals" count={stats?.new_proposals_month} value={stats?.pending_proposal_value ? fmtMoney(stats.pending_proposal_value) : null} onClick={() => navigate("/proposals")} testid="chip-view-proposals" />
              <ActionChip label="Sales Decks" onClick={() => navigate("/resources?tab=sales_deck")} testid="chip-sales-decks" />
              <ActionChip label="Level Up Your Skills" onClick={() => navigate("/resources?tab=training")} testid="chip-training" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RIGHT COLUMN — Messages + Tasks */}
      <div className="lg:col-span-2">
        <MessagesTasksPanel conversations={conversations} tasks={tasks} navigate={navigate} />
      </div>
    </div>
  );
}

function AdminDashboard({ user, stats, conversations, setConversations, tasks, navigate, recentProposals }) {
  // `recentOrders` is now a MIXED list of new orders and change orders,
  // merged + sorted by created_at desc. Each row carries a `kind` field so
  // the UI can render the right chip ("NEEDS VERIFY", "VERIFIED", or "CHANGE
  // ORDER") and route clicks to the right place.
  const [recentOrders, setRecentOrders] = useState([]);
  const [allUnverified, setAllUnverified] = useState([]);
  const [unverifiedOpen, setUnverifiedOpen] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  // Which Task Board group is expanded. Only one open at a time keeps the
  // widget scannable; users click again to collapse.
  const [expandedTaskClient, setExpandedTaskClient] = useState(null);
  useEffect(() => {
    if (user?.role === "super_admin") {
      // Fetch new orders + change orders in parallel so the "Recent New &
      // Change Orders" tile shows both streams without a visible lag.
      import("../lib/api").then(({ getProjects }) => {
        Promise.all([getProjects(), getChangeRequests()])
          .then(([projRes, crRes]) => {
            const projects = (projRes.data || []).map((p) => ({ ...p, kind: "order" }));
            const changes = (crRes.data || []).map((cr) => ({
              ...cr,
              kind: "change_order",
              // Normalize so the row renderer can speak one schema.
              id: cr.id,
              created_at: cr.created_at,
            }));
            const isActiveProject = (p) =>
              !["completed", "archived", "cancelled"].includes((p.status || "").toLowerCase());
            // Combined newest-first list of new orders + open/in-progress change orders.
            const combined = [
              ...projects,
              ...changes.filter((cr) => cr.status !== "completed"),
            ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
            setRecentOrders(combined.slice(0, 8));
            // The "N to verify" badge still counts new orders only — change
            // orders have their own workflow and don't go through verification.
            setAllUnverified(projects.filter((p) => p.verified === false && isActiveProject(p)));
          });
      });
    }
  }, [user]);

  const unverifiedCount = stats?.pending_verification_count ?? 0;
  // When the admin clears orders from inside the modal, the stats endpoint
  // won't re-fetch until the dashboard remounts — show the live local count
  // so the badges reflect reality instantly.
  const liveUnverifiedCount = allUnverified.length || unverifiedCount;

  const handleInlineVerify = async (e, projectId) => {
    e.stopPropagation();
    e.preventDefault();
    setVerifyingId(projectId);
    try {
      await verifyProject(projectId);
      setAllUnverified((prev) => prev.filter((p) => p.id !== projectId));
      setRecentOrders((prev) => prev.map((p) => (p.id === projectId ? { ...p, verified: true } : p)));
      toast.success("Order verified — Ops Coordinator notified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify order");
    } finally {
      setVerifyingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Recent Orders — newest first, regardless of verified state.
            Unverified ones get an amber "Needs Verify" pill + inline CTA. */}
        <Card className="border bg-gradient-to-br from-amber-50 to-yellow-50">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-outfit font-semibold flex items-center gap-2 text-amber-900">
                <CheckCircle2 className="h-4 w-4" /> Recent New &amp; Change Orders
              </h3>
              {liveUnverifiedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setUnverifiedOpen(true)}
                  className="text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-full px-2.5 py-0.5 font-semibold transition inline-flex items-center gap-1 shadow-sm"
                  title={`Show all ${liveUnverifiedCount} orders awaiting verification`}
                  data-testid="unverified-count-badge"
                >
                  {liveUnverifiedCount} to verify
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </div>
            {recentOrders.length === 0 ? (
              <p className="text-xs text-muted-foreground">No orders yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="recent-orders-list">
                {recentOrders.map((p) => {
                  const isChange = p.kind === "change_order";
                  const needsVerify = !isChange && p.verified === false;
                  const typeLabel = isChange
                    ? ({
                        marketing: "Marketing Change",
                        website: "Website Change",
                        seo_aeo: "SEO/AEO Update",
                        cancellation: "Cancellation",
                      }[p.request_type] || "Change")
                    : "";
                  // Change orders deep-link into the related order's change-
                  // request anchor. If no project_id is attached (client-level
                  // change), fall back to Monthly Active Clients.
                  const onClick = () => {
                    if (isChange) {
                      if (p.project_id) navigate(`/orders/${p.project_id}#change-requests`);
                      else navigate("/clients/monthly-active");
                    } else {
                      navigate(`/orders/${p.id}`);
                    }
                  };
                  return (
                    <button
                      key={`${p.kind}-${p.id}`}
                      type="button"
                      onClick={onClick}
                      data-testid={`${isChange ? "recent-change-order-row" : "recent-order-row"}-${p.id}`}
                      className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm hover:shadow-sm transition ${
                        isChange
                          ? "bg-white border-sky-300 hover:bg-sky-50"
                          : needsVerify
                            ? "bg-white border-amber-300 hover:bg-amber-50"
                            : "bg-white/70 border-zinc-200 hover:bg-zinc-50"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate">{p.client_name || p.client_company || "Unknown"}</span>
                          {isChange ? (
                            <span className="text-[10px] font-semibold text-sky-800 bg-sky-100 rounded-full px-1.5 py-0.5 shrink-0">CHANGE ORDER</span>
                          ) : needsVerify ? (
                            <span className="text-[10px] font-semibold text-amber-800 bg-amber-200 rounded-full px-1.5 py-0.5 shrink-0">NEEDS VERIFY</span>
                          ) : (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5 shrink-0">VERIFIED</span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {isChange
                            ? `${typeLabel} · ${p.agency_name || "—"} · ${timeAgo(p.created_at)}`
                            : `${(p.product_types || [p.product_type]).filter(Boolean).join(", ")} · ${p.agency_name || "—"} · ${timeAgo(p.created_at)}`}
                        </div>
                        <div className="text-[11px] text-muted-foreground" data-testid={`recent-row-stamp-${p.id}`}>
                          {formatStamp(p.created_at)}
                        </div>
                      </div>
                      <div className={`text-xs font-semibold shrink-0 ${isChange ? "text-sky-700" : needsVerify ? "text-amber-800" : "text-zinc-600"}`}>
                        {isChange ? "" : (p.contract_value ? fmtMoney(p.contract_value) : "")}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reply Needed (Messages) */}
        <Card className="border bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-outfit font-semibold flex items-center gap-2 text-orange-900">
                <MessageSquare className="h-4 w-4" /> Reply Needed
                {conversations.filter((c) => c.reply_needed).length > 0 && (
                  <span className="text-[11px] bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold">
                    {conversations.filter((c) => c.reply_needed).length}
                  </span>
                )}
              </h3>
              <button type="button" onClick={() => navigate("/messages")} className="text-xs text-orange-700 hover:underline" data-testid="admin-open-messages">Open Inbox →</button>
            </div>
            {conversations.filter((c) => c.reply_needed).length === 0 ? (
              <p className="text-xs text-muted-foreground">All caught up — no replies needed.</p>
            ) : (
              <div className="space-y-1.5">
                {conversations.filter((c) => c.reply_needed).slice(0, 5).map((c) => (
                  <div
                    key={c.client_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/messages/${c.client_id}`)}
                    onKeyDown={(e) => { if (e.key === "Enter") navigate(`/messages/${c.client_id}`); }}
                    data-testid={`admin-msg-${c.client_id}`}
                    className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white hover:bg-orange-100/40 border border-orange-100 text-sm cursor-pointer"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.client_name}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {[c.agency_name, c.rep_name].filter(Boolean).join(" · ")}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate mt-0.5"><b>{c.last_author_name}</b>: {c.last_body}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] text-muted-foreground">{timeAgo(c.last_activity)}</span>
                      {c.primary_project_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/orders/${c.primary_project_id}`); }}
                          title="Open the order for this client"
                          className="inline-flex items-center justify-center h-5 px-1.5 rounded-full border border-zinc-300 hover:bg-sky-50 hover:border-sky-300 text-[10px] font-medium text-zinc-700 hover:text-sky-800 transition"
                          data-testid={`reply-needed-view-order-${c.client_id}`}
                        >
                          View Order →
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await markClientHandled(c.client_id);
                            setConversations((prev) => prev.map((x) => x.client_id === c.client_id ? { ...x, reply_needed: false, is_handled: true } : x));
                          } catch (err) {
                            console.error("mark-handled failed", err);
                          }
                        }}
                        title="Mark this conversation as handled (no reply needed)"
                        className="inline-flex items-center justify-center h-5 px-1.5 rounded-full border border-zinc-300 hover:bg-emerald-50 hover:border-emerald-300 text-[10px] font-medium text-zinc-600 hover:text-emerald-700 transition"
                        data-testid={`admin-mark-handled-${c.client_id}`}
                      >
                        ✓ Handled
                      </button>
                      <span className="inline-flex items-center justify-center h-5 rounded-full bg-red-500 text-white text-[10px] font-semibold px-2">Reply</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Proposal/Audit Requests — only NEW (not yet actioned). Pending Proposals link below. */}
        <Card className="border bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-outfit font-semibold flex items-center gap-2 text-indigo-900">
                <FileSearch className="h-4 w-4" /> New Proposal & Audit Requests
                {recentProposals.length > 0 && (
                  <span className="text-[11px] bg-indigo-600 text-white rounded-full px-2 py-0.5 font-semibold">{recentProposals.length}</span>
                )}
              </h3>
            </div>
            {recentProposals.length === 0 ? (
              <p className="text-xs text-muted-foreground">No new requests — pipeline is clear. 🎉</p>
            ) : (
              <div className="space-y-1.5">
                {recentProposals.slice(0, 5).map((p) => {
                  // Compute "due in X days" from due_at ISO
                  let dueLabel = null;
                  if (p.due_at) {
                    const due = new Date(p.due_at);
                    const diffH = Math.round((due.getTime() - Date.now()) / 3600000);
                    if (diffH < 0) dueLabel = `${Math.abs(Math.round(diffH/24))}d overdue`;
                    else if (diffH < 24) dueLabel = `Due in ${diffH}h`;
                    else dueLabel = `Due in ${Math.round(diffH/24)}d`;
                  }
                  return (
                    <button key={p.id} type="button" onClick={() => navigate(`/proposals?open=${p.id}`)} data-testid={`new-proposal-${p.id}`} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white hover:bg-indigo-100/40 border border-indigo-100 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.client_name || p.message || "Unknown"}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {[p.agency_name, p.rep_name].filter(Boolean).join(" · ") || p.industry || ""}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{fmtMoney(p.estimated_value)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {dueLabel && (
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${dueLabel.includes("overdue") ? "bg-red-500 text-white" : "bg-indigo-100 text-indigo-800"}`}>
                            {dueLabel}
                          </span>
                        )}
                        <Badge variant="outline" className="shrink-0 text-[10px]">New</Badge>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="mt-3 pt-3 border-t">
              <button type="button" onClick={() => navigate("/proposals")} className="text-xs text-indigo-700 hover:underline font-medium" data-testid="view-pending-proposals-link">
                View Pending Proposals →
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Task Board — grouped by client */}
        <Card className="border bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-outfit font-semibold flex items-center gap-2 text-emerald-900">
                <ListChecks className="h-4 w-4" /> Task Board
                {tasks.length > 0 && (
                  <span className="text-[11px] bg-emerald-600 text-white rounded-full px-2 py-0.5 font-semibold">
                    {tasks.length}
                  </span>
                )}
              </h3>
            </div>
            {tasks.length === 0 ? (
              <p className="text-xs text-muted-foreground">No pending tasks.</p>
            ) : (
              <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1" data-testid="task-board-list">
                {(() => {
                  // Group tasks by client → one row per client with task count + product chips
                  const grouped = {};
                  tasks.forEach((t) => {
                    const key = t.client_id || `__no_client_${t.id}`;
                    if (!grouped[key]) {
                      grouped[key] = {
                        client_id: t.client_id,
                        client_name: t.client_name,
                        agency_name: t.agency_name,
                        rep_name: t.rep_name,
                        project_id: t.project_id,
                        assignee: t.assignee,
                        product_types: new Set(),
                        tasks: [],
                        next_due: t.due_date,
                      };
                    }
                    if (t.product_type) grouped[key].product_types.add(t.product_type);
                    grouped[key].tasks.push(t);
                    if (t.due_date && (!grouped[key].next_due || t.due_date < grouped[key].next_due)) {
                      grouped[key].next_due = t.due_date;
                    }
                  });
                  const rows = Object.values(grouped);
                  return rows.map((g) => {
                    const key = g.client_id || g.project_id;
                    const isOpen = expandedTaskClient === key;
                    return (
                      <div
                        key={key}
                        data-testid={`task-client-${key}`}
                        className="rounded-md bg-white border border-emerald-100 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedTaskClient(isOpen ? null : key)}
                          aria-expanded={isOpen}
                          data-testid={`task-client-toggle-${key}`}
                          className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 hover:bg-emerald-100/40 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">{g.client_name || "—"}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                              {[g.agency_name, g.rep_name].filter(Boolean).join(" / ") || "—"}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {[...g.product_types].map((p) => (
                                <span key={p} className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-medium border border-emerald-200">
                                  {p.replace("_", " ")}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="inline-flex items-center justify-center h-5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold px-2">
                              {g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}
                            </span>
                            <span className="text-[10px] text-emerald-800 font-medium">{g.assignee || "—"}</span>
                          </div>
                          <ChevronRight
                            className={`h-4 w-4 text-emerald-700 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                          />
                        </button>
                        {isOpen && (
                          <div
                            className="border-t border-emerald-100 bg-emerald-50/40 px-3 py-2 space-y-1"
                            data-testid={`task-client-details-${key}`}
                          >
                            {g.tasks.map((t) => (
                              <div
                                key={t.id}
                                className="flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded bg-white border border-emerald-100"
                                data-testid={`task-row-${t.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-foreground flex items-center gap-1.5">
                                    <span className="truncate">{t.title || "Untitled task"}</span>
                                    <TaskFreshnessPill status={t.status} dueDate={t.due_date} />
                                  </div>
                                  <div className="text-[10px] text-muted-foreground truncate">
                                    {[
                                      t.type_label,
                                      t.status,
                                      t.due_date ? `due ${new Date(t.due_date).toLocaleDateString()}` : null,
                                    ].filter(Boolean).join(" · ")}
                                  </div>
                                </div>
                                {g.project_id && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/orders/${g.project_id}`); }}
                                    className="text-[10px] font-semibold text-emerald-700 hover:text-emerald-900 hover:underline shrink-0"
                                    data-testid={`task-row-open-${t.id}`}
                                  >
                                    Open →
                                  </button>
                                )}
                              </div>
                            ))}
                            {g.project_id && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate(`/orders/${g.project_id}`); }}
                                className="w-full mt-1 text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 hover:bg-emerald-100/60 py-1.5 rounded transition"
                                data-testid={`task-client-open-order-${key}`}
                              >
                                Open Order Detail →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Renewals (full-width) */}
      <Card className="border bg-gradient-to-br from-rose-50 to-orange-50">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-outfit font-semibold flex items-center gap-2 text-rose-900">
              <Repeat className="h-4 w-4" /> Upcoming Renewals
            </h3>
            <button type="button" onClick={() => navigate("/renewals")} className="text-xs text-rose-700 hover:underline">View All →</button>
          </div>
          <p className="text-xs text-muted-foreground">
            {(stats?.expiring_count ?? 0)} contract{(stats?.expiring_count ?? 0) === 1 ? "" : "s"} ending in next 30 days
            {(stats?.expiring_value ?? 0) > 0 && <> · Total Value <b>{fmtMoney(stats.expiring_value)}</b></>}
          </p>
        </CardContent>
      </Card>

      {/* Full "Needs Verify" list — surfaced from the pill click so the admin
          can eyeball every unverified order without paging through the
          6-row summary card. */}
      <Dialog open={unverifiedOpen} onOpenChange={setUnverifiedOpen}>
        <DialogContent className="max-w-2xl" data-testid="unverified-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <CheckCircle2 className="h-4 w-4" />
              Orders Awaiting Verification
              <span className="text-xs bg-amber-500 text-white rounded-full px-2 py-0.5 font-semibold">
                {allUnverified.length}
              </span>
            </DialogTitle>
            <DialogDescription>
              Click any order to open it and mark it verified.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-1.5 pr-1" data-testid="unverified-modal-list">
            {allUnverified.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Nothing left to verify — nice work.
              </p>
            ) : (
              allUnverified.map((p) => (
                <div
                  key={p.id}
                  data-testid={`unverified-row-${p.id}`}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border bg-white hover:bg-amber-50 hover:border-amber-300 transition"
                >
                  <button
                    type="button"
                    onClick={() => { setUnverifiedOpen(false); navigate(`/orders/${p.id}`); }}
                    className="min-w-0 flex-1 text-left"
                    data-testid={`unverified-row-open-${p.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{p.client_name || p.client_company || "Unknown"}</span>
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-200 rounded-full px-1.5 py-0.5 shrink-0">NEEDS VERIFY</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {(p.product_types || [p.product_type]).filter(Boolean).join(", ")} · {p.agency_name || "—"} · {timeAgo(p.created_at)}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    {p.contract_value ? (
                      <div className="text-xs font-semibold text-amber-800">{fmtMoney(p.contract_value)}</div>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      disabled={verifyingId === p.id}
                      onClick={(e) => handleInlineVerify(e, p.id)}
                      className="h-7 px-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                      data-testid={`unverified-row-verify-${p.id}`}
                    >
                      {verifyingId === p.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <><Check className="h-3.5 w-3.5 mr-1" /> Verify</>
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentProposals, setRecentProposals] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, actRes, convRes, tasksRes] = await Promise.all([
          getDashboardStats().catch(() => ({ data: {} })),
          getRecentActivity().catch(() => ({ data: { proposals: [] } })),
          getMessageConversations().catch(() => ({ data: [] })),
          getTaskBoard(50).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setRecentProposals(actRes.data?.proposals || []);
        setConversations(convRes.data || []);
        setTasks(tasksRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  // Role routing:
  // - rep, manager: rep-style 3-box layout (manager rows include rep names baked into MessagesTasksPanel via conversations)
  // - super_admin, ops_coordinator: 5-box admin layout
  const isAdminView = user?.role === "super_admin" || user?.role === "ops_coordinator";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Welcome back, {user?.name?.split(" ")[0] || "there"} 👋</h1>
        <p className="text-muted-foreground mt-1">
          {user?.agency_name ? `${user.agency_name} · ` : ""}
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </p>
      </div>

      <GlobalSearchBar className="max-w-3xl" />

      {isAdminView ? (
        <AdminDashboard user={user} stats={stats} conversations={conversations} setConversations={setConversations} tasks={tasks} navigate={navigate} recentProposals={recentProposals} />
      ) : (
        <RepDashboard user={user} stats={stats} conversations={conversations} tasks={tasks} navigate={navigate} />
      )}
    </div>
  );
}
